import { PlusOutlined, WarningOutlined } from "@ant-design/icons";
import {
  Spin,
  Row,
  Col,
  Button,
  Tooltip,
  Typography,
  Divider,
  Modal,
  List,
  TablePaginationConfig,
} from "antd";
import IconInfinite from "assets/images/infinite";
import { Fragment } from "react/jsx-runtime";
import DistributionPointTable from "./components/DistributionPointTable";
import { useEffect, useMemo, useState, useCallback } from "react";
import {
  DistributionPointResponseResource,
  getPageableDistributionPoints,
  entitlementToRegister,
  getSubscription,
  SubscriptionDetails,
} from "api/generated";
import "../../routes/register/styles.css";
import { useClient } from "hooks/useClient";
import { CV_CONTEXT_PATH, getIsComanyAdministrator } from "security";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { usePaginationTable } from "hooks/commons/usePaginationTable";
import { FilterValue, SorterResult } from "antd/lib/table/interface";
import { querySerializer } from "common/helpers/queryUtils";
import { handleAxiosError } from "common/helpers/generalUtils";
import { useDistributionPoints } from "hooks/distributionPoints/useDistributionPoints";

function DistributionPointScreen(props: RouteComponentProps) {
  const { client } = useClient();
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [spinnerSubscription, setSpinnerSubscription] = useState(false);
  const [subscription, setSubscription] = useState<SubscriptionDetails>();
  const { onSelectDistributionPointId } = useDistributionPoints();

  const [filterEntitled, setFilterEntitled] = useState<boolean>();
  const pagination =
    usePaginationTable<DistributionPointResponseResource>(props);
  const query = useMemo(
    () =>
      pagination.debouncedFilterText.length >= 1
        ? pagination.debouncedFilterText
        : undefined,
    [pagination.debouncedFilterText],
  );

  const isCompanyAdministrator: boolean = useMemo(() => {
    return getIsComanyAdministrator();
  }, []);

  const isSubscriptionOnFreeMode = useMemo(() => {
    return (
      subscription?.subscriptionMessages &&
      subscription?.subscriptionMessages.find(
        (x) => x === "SUBSCRIPTIONS_ON_FREE_MODE",
      )
    );
  }, [subscription]);

  // Función para obtener puntos de distribución
  const fetchDistributionPoints = useCallback(() => {
    setLoadingSpinner(true);
    getPageableDistributionPoints({
      client,
      query: {
        textFilter: query,
        entitled: filterEntitled,
        pageRequest: {
          page: pagination.currentPage,
          size: pagination.pageSize,
          sort: pagination.sort,
        },
      },
      querySerializer: querySerializer,
    })
      .then(({ data }) => {
        pagination.setTotalItems(data?.totalElements ?? 0);
        pagination.setItems(data?.content ?? []);
      })
      .catch((err) =>
        handleAxiosError(err, "No fue posible obtener puntos de distribución"),
      )
      .finally(() => {
        setLoadingSpinner(false);
      });
  }, [
    client,
    pagination.currentPage,
    pagination.pageSize,
    pagination.sort,
    query,
    filterEntitled,
  ]);

  // Función para obtener la suscripción
  const fetchSubscription = useCallback(() => {
    getSubscription({ client })
      .then(({ data }) => {
        setSubscription(data);
      })
      .catch((err) => handleAxiosError(err, "Error al obtener la suscripción"));
  }, [client]);

  function showNewDistributionPointForm() {
    setSpinnerSubscription(true);
    entitlementToRegister({ client })
      .then(({ data }) => {
        if (data?.entitlement) {
          props.history.push("distributionPoints/new");
        } else {
          Modal.error({
            title: "Suscripción: Excepción al registrar punto de distribución",
            icon: <WarningOutlined />,
            content: (
              <List>
                {data?.subscriptionDetails?.subscriptionMessageList?.map(
                  (error) => {
                    return <List.Item key={error}>{error}</List.Item>;
                  },
                )}
              </List>
            ),
            onOk: () => {
              window.location.replace(`${CV_CONTEXT_PATH}/subscription`);
            },
          });
        }
      })
      .finally(() => {
        setSpinnerSubscription(false);
      });
  }

  // Función para manejar cambios en la tabla
  function handleTableChange(
    _pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter:
      | SorterResult<DistributionPointResponseResource>
      | SorterResult<DistributionPointResponseResource>[],
  ) {
    // Manejar filtros
    const entitledFilter = filters.entitled;
    if (entitledFilter && entitledFilter.length === 1) {
      const isEntitled = entitledFilter[0] as boolean;
      setFilterEntitled(isEntitled);
    } else {
      setFilterEntitled(undefined);
    }

    // Manejar ordenamiento
    if (!Array.isArray(sorter) && sorter.field && sorter.order) {
      const { field, order } = sorter;
      const direction = order === "ascend" ? "ASC" : "DESC";

      // Mapeo de campos de ordenamiento
      const sortMapping: Record<string, string> = {
        subsidiary: "subsidiary",
        senerLicense: "senerLicense",
      };

      const sortField = sortMapping[field as string] || "subsidiary";

      pagination.setSort([
        `${sortField === "subsidiary" ? "name" : sortField},${direction}`,
      ]);
    } else {
      // Reset al ordenamiento por defecto si no hay ordenamiento activo
      pagination.setSort(["name,DESC"]);
    }
  }

  // Función para refrescar todos los datos
  const refreshData = useCallback(() => {
    fetchSubscription();
    fetchDistributionPoints();
  }, [fetchSubscription, fetchDistributionPoints]);

  // Efectos iniciales
  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  useEffect(() => {
    fetchDistributionPoints();
  }, [fetchDistributionPoints]);

  return (
    <Fragment>
      <Spin size="large" tip="Cargando..." spinning={loadingSpinner}>
        <div>
          <div style={{ marginBottom: "24px" }}>
            <Row>
              <Col lg={12} xs={24}>
                <Button
                  hidden={!isCompanyAdministrator}
                  className="customized-primary"
                  type="primary"
                  icon={<PlusOutlined />}
                  loading={spinnerSubscription}
                  onClick={showNewDistributionPointForm}
                >
                  Agregar Punto de Distribución
                </Button>
              </Col>
              <Col
                lg={12}
                xs={24}
                style={{
                  display: "flex",
                  placeContent: "flex-end",
                }}
              >
                <Tooltip title="Suscripción">
                  <Typography.Title code level={3}>
                    <Typography.Text style={{ fontSize: "17px" }}>
                      Puntos de Distribución &nbsp;
                    </Typography.Text>
                    {subscription?.activeDistributionPoints}/
                    {isSubscriptionOnFreeMode ? (
                      <IconInfinite />
                    ) : (
                      subscription?.equipmentsNumber
                    )}
                  </Typography.Title>
                </Tooltip>
              </Col>
            </Row>
          </div>
          <Row gutter={24}>
            <DistributionPointTable
              distributionPointList={pagination.items}
              refreshOnUpdate={refreshData}
              pagination={{
                ...pagination.paginationConfig,
                onChange: pagination.onPageChange,
                onShowSizeChange: pagination.onPageSizeChange,
              }}
              handleTableChange={handleTableChange}
              handleSearchChange={(value: string) => {
                pagination.setCurrentPage(0);
                pagination.setFilterText(value);
              }}
              onSelectDistributionPointId={onSelectDistributionPointId}
              client={client}
            />
          </Row>
        </div>

        <Divider />
      </Spin>
    </Fragment>
  );
}

export default withRouter(DistributionPointScreen);
